import React from 'react';
import { Column } from 'react-table';
import SessionTable, {
  ResponseGuard,
  SessionTableInstanceProps
} from '../index';
import { CellFunction, RESPONSE_NA, RESPONSE_NONE } from '../../cells';
import { Capitalize } from '../../../../../libs/format';
import {
  IEpxSDOH,
  SDOHModuleKeyPrefix,
  SDOHModuleKeyToPrefix,
  SDOHChildMapping
} from '../../../../../libs/apps/epxsdoh';

const TRI_STATES = {
  LIKELY: {
    1: 'Unlikely',
    2: 'Somewhat Likely',
    3: 'Very Likely'
  },
  FREQUENCY: {
    1: 'Never',
    2: 'Sometimes',
    3: 'Often'
  },
  DIFFICULTY: {
    1: 'Not Hard',
    2: 'Somewhat Hard',
    3: 'Very Hard'
  },
  HOUSING: {
    1: 'Stable Housing',
    2: 'Unstable Housing',
    3: 'No Housing'
  },
  WORRY: {
    1: 'Not Worried',
    2: 'Somewhat Worried',
    3: 'Very Worried'
  }
};

interface SegmentOutput {
  label: string;
  value: string;
}

const GetSegment = (
  label: string,
  value: number,
  mapping: Record<number, string>
) => {
  return {
    label,
    value: value != null && mapping[value] != null ? mapping[value] : '?'
  };
};

type SegmentOutputFunction<K extends keyof SDOHChildMapping> = (
  child: SDOHChildMapping[K]
) => SegmentOutput[];

type SegmentOutputFunctions = {
  [key in keyof SDOHChildMapping]: SegmentOutputFunction<key>;
};

const ModuleOutputs: SegmentOutputFunctions = {
  [SDOHModuleKeyPrefix.Housing]: (child) => {
    const segments: SegmentOutput[] = [
      GetSegment('Housing Security', child.security, TRI_STATES.HOUSING)
    ];
    if (child.security === 2 || child.security === 3) {
      segments.push(
        GetSegment('Housing Displaced', child.displaced, TRI_STATES.LIKELY)
      );
    }
    segments.push(GetSegment('Pests', child.pests, TRI_STATES.LIKELY));
    return segments;
  },
  [SDOHModuleKeyPrefix.Food]: (child) => {
    const segments: SegmentOutput[] = [
      GetSegment('Out of Food', child.scarcity, TRI_STATES.LIKELY),
      GetSegment('Ration Food', child.ration, TRI_STATES.LIKELY)
    ];
    return segments;
  },
  [SDOHModuleKeyPrefix.Safety]: (child) => {
    const segments: SegmentOutput[] = [
      GetSegment('Feel Unsafe', child.unsafe, TRI_STATES.FREQUENCY)
    ];
    return segments;
  },
  [SDOHModuleKeyPrefix.Transportation]: (child) => {
    const segments: SegmentOutput[] = [
      GetSegment('Reliable Transport', child.reliable, TRI_STATES.WORRY)
    ];
    return segments;
  },
  [SDOHModuleKeyPrefix.Utilities]: (child) => {
    const segments: SegmentOutput[] = [
      GetSegment('Utility Shut Off', child.shutOffService, TRI_STATES.LIKELY)
    ];
    return segments;
  },
  [SDOHModuleKeyPrefix.Support]: (child) => {
    const segments: SegmentOutput[] = [
      GetSegment('Lonely/Isolation', child.isolation, TRI_STATES.FREQUENCY),
      GetSegment('Not Getting Help', child.help, TRI_STATES.FREQUENCY)
    ];
    return segments;
  },
  [SDOHModuleKeyPrefix.Employment]: (child) => {
    const segments: SegmentOutput[] = [
      GetSegment('Trouble w/ Employment', child.unemployment, TRI_STATES.LIKELY)
    ];
    return segments;
  },
  [SDOHModuleKeyPrefix.HealthLiteracy]: (child) => {
    const segments: SegmentOutput[] = [
      GetSegment('Health Literacy', child.understanding, TRI_STATES.DIFFICULTY)
    ];
    return segments;
  },
  [SDOHModuleKeyPrefix.FinancialStrain]: (child) => {
    const segments: SegmentOutput[] = [
      GetSegment('Financial Strain', child.strain, TRI_STATES.DIFFICULTY)
    ];
    return segments;
  },
  [SDOHModuleKeyPrefix.Insurance]: (child) => {
    const segments: SegmentOutput[] = [
      GetSegment('No Health Insurance', child.uninsured, TRI_STATES.LIKELY)
    ];
    return segments;
  }
};

const SDOHCell: CellFunction<IEpxSDOH> = ({ row: { original: session } }) => {
  let segments: SegmentOutput[] = [];
  for (const module of session.modules) {
    const prefix = SDOHModuleKeyToPrefix[module];
    if (ModuleOutputs[prefix] && session[prefix]) {
      segments = segments.concat(ModuleOutputs[prefix](session[prefix]));
    }
  }
  if (segments.length === 0 && session._urgentNeeds) {
    return (
      <span>
        <b>Urgent Needs Only</b>
      </span>
    );
  }
  const nodes: React.ReactNodeArray = [];
  for (const [i, segment] of segments.entries()) {
    nodes.push(
      <span key={i * 2}>
        <b>{segment.label}:</b> {segment.value}
      </span>
    );
    if (i !== segments.length - 1) {
      nodes.push(
        <span key={i * 2 + 1}>
          {' '}
          <b>|</b>{' '}
        </span>
      );
    }
  }
  if (nodes.length === 0) {
    return <span>None Reported</span>;
  }
  return <span>{nodes}</span>;
};

export const UrgentNeedsCell: CellFunction<IEpxSDOH> = ({
  row: { original: session }
}) => {
  const child = session._urgentNeeds;
  if (!child) {
    return RESPONSE_NA;
  }
  if (child.issues == null) {
    return RESPONSE_NONE;
  }
  if (child.issues.length === 0) {
    return <span>None</span>;
  }
  const output = child.issues.map((issue) => Capitalize(issue)).join(', ');
  return <span>{output}</span>;
};

const EpxSDOHTable: React.FC<SessionTableInstanceProps> = (
  props
): JSX.Element => {
  const columns: Column<IEpxSDOH>[] = [
    {
      Header: 'Social Determinants',
      id: 'sdoh',
      Cell: ResponseGuard(SDOHCell)
    },
    {
      Header: 'Urgent Needs',
      id: 'urgentNeeds',
      Cell: ResponseGuard(UrgentNeedsCell)
    }
  ];
  return (
    <SessionTable<IEpxSDOH> url="/epxsdoh/data/" columns={columns} {...props} />
  );
};

export default EpxSDOHTable;
